/**
 * Store list labels — always last 30 days vs prior 30 days (same window as the table).
 * Every active store gets "Active" plus exactly one performance label (or only Active).
 */

const BADGE_TONE = {
  emerald: 'bg-emerald-500/15 text-emerald-700',
  sky: 'bg-sky-500/15 text-sky-800',
  violet: 'bg-violet-500/15 text-violet-800',
  orange: 'bg-orange-500/15 text-orange-800',
  amber: 'bg-amber-500/15 text-amber-800',
  slate: 'bg-slate-500/15 text-slate-700',
  rose: 'bg-rose-500/15 text-rose-800',
  indigo: 'bg-indigo-500/15 text-indigo-800',
};

export function salesRankPercentile() {
  return 0;
}

function badge(label) {
  return { ...label, className: BADGE_TONE[label.tone] || BADGE_TONE.slate };
}

const ACTIVE = { key: 'active', label: 'Active', tone: 'emerald' };

/**
 * One performance tag from month-over-month % (30d vs prior 30d).
 * Priority: top > growing > trending > high priority > under > moderate/stable
 */
export function pickSecondaryPerformanceLabel(summary) {
  const delta = summary?.deltaPct || {};
  const salesDelta = delta.sales;
  const spendDelta = delta.spend;
  const cur = summary?.current || {};
  const spend = Number(cur.spend) || 0;
  const sales = Number(cur.sales) || 0;
  const acos = cur.acos != null ? Number(cur.acos) : null;

  if (salesDelta != null && salesDelta >= 10) {
    return { key: 'top', label: 'Top performer', tone: 'sky' };
  }
  if (salesDelta != null && salesDelta >= 5) {
    return { key: 'growing', label: 'Growing', tone: 'violet' };
  }
  if (spendDelta != null && spendDelta >= 3) {
    return { key: 'trending', label: 'Trending', tone: 'orange' };
  }
  if (salesDelta != null && salesDelta <= -10) {
    return { key: 'high_priority', label: 'High priority', tone: 'amber' };
  }
  if (salesDelta != null && salesDelta <= -5) {
    return { key: 'under', label: 'Underperforming', tone: 'rose' };
  }
  if (salesDelta != null && salesDelta >= -2 && salesDelta <= 2) {
    return { key: 'moderate', label: 'Moderately performing', tone: 'indigo' };
  }
  if (
    (salesDelta == null || (salesDelta > -2 && salesDelta < 5))
    && (spendDelta == null || Math.abs(spendDelta) < 3)
    && spend > 0
  ) {
    return { key: 'stable', label: 'Stable', tone: 'slate' };
  }
  if (spend >= 500 && acos != null && acos > 45) {
    return { key: 'high_priority', label: 'High priority', tone: 'amber' };
  }

  return null;
}

/** Active + 0–1 performance badge */
export function getStorePerformanceBadges(summary) {
  if (!summary?.current) return [];

  const spend = Number(summary.current.spend) || 0;
  const sales = Number(summary.current.sales) || 0;
  if (spend <= 0 && sales <= 0) {
    return [badge({ key: 'inactive', label: 'No ad activity', tone: 'slate' })];
  }

  const out = [badge(ACTIVE)];
  const secondary = pickSecondaryPerformanceLabel(summary);
  if (secondary) out.push(badge(secondary));
  return out;
}

export { BADGE_TONE };
